import React, { useState, useEffect } from "react"
import { Button, Flex, Input, Checkbox, Badge } from "antd"
import { SearchOutlined, DownOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import MultiSelect from "components/common/MultiSelect"
import CustomDrawer from "ui/Drawer"
import PermissionGate from "components/PermissionGate"
import { MODULES, ACTIONS } from "utils/permissions"
import styles from "./index.module.css"
import IconWithBg from "ui/IconWithBg"
import { FilterIcon } from "icons"
import useActiveGuide from "hooks/useActiveClient"

const EquipmentFilters = ({
  search,
  onSearchChange,
  selectedCategories,
  onCategoryChange,
  categoryOptions,
  onManageCategoriesClick,
  onAddEquipmentClick
}) => {
  const { t } = useTranslation()
  const [isMobile, setIsMobile] = useState(false)
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false)
  const { isActive: active, isPending } = useActiveGuide()

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkScreenSize()
    window.addEventListener("resize", checkScreenSize)
    return () => window.removeEventListener("resize", checkScreenSize)
  }, [])

  const handleCategorySelect = (categoryValue) => {
    const allValues = categoryOptions.map((o) => o.value)
    const nonAllValues = categoryOptions.filter((o) => o.value !== "all").map((o) => o.value)

    if (categoryValue === "all") {
      // Toggle all selection
      if (selectedCategories.includes("all")) {
        onCategoryChange([])
      } else {
        onCategoryChange(allValues)
      }
      return
    }

    const set = new Set(selectedCategories.filter((v) => v !== "all"))

    if (set.has(categoryValue)) {
      // Deselect
      set.delete(categoryValue)
    } else {
      // Select
      set.add(categoryValue)
    }

    const current = Array.from(set)
    const allNonAllSelected = nonAllValues.every((v) => set.has(v))
    onCategoryChange(allNonAllSelected ? allValues : current)
  }

  const getCategoryDisplayText = () => {
    if (selectedCategories.length === 0) {
      return t("Category") || "Category"
    }
    if (selectedCategories.includes("all") || selectedCategories.length === categoryOptions.length) {
      return `${t("Category") || "Category"} (All)`
    }
    return `${t("Category") || "Category"} (${selectedCategories.filter((v) => v !== "all").length})`
  }

  return (
    <>
      <Flex justify="space-between" gap={10} className={styles.filterWrap}>
        <Flex gap={16} align="center">
          <Input
            id="search"
            type="text"
            placeholder={t("listings.tabs.resources.equipment_placeholder") || "Search equipment..."}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className={styles.searchInput}
            prefix={<SearchOutlined aria-hidden="true" />}
            allowClear
            aria-label={t("clients.search_clients") || "Search"}
          />
          {isMobile ? (
            <div onClick={() => setCategoryDrawerOpen(true)}>
              <Badge count={selectedCategories.length} size="default" offset={[-5, 0]} className={styles.categoryBadge}>
                <IconWithBg
                  className={styles.categoryButton}
                  height={40}
                  width={40}
                  icon={<FilterIcon className={styles.categoryFilterButton} />}
                />
              </Badge>
            </div>
          ) : (
            <MultiSelect
              value={selectedCategories}
              onChange={onCategoryChange}
              options={categoryOptions}
              placeholder={t("Category") || "Category"}
              dropdownClassName={styles.dropdownButton}
            />
          )}
        </Flex>
        <Flex gap={16} align="center" className={`ml-auto ${styles.filterActions}`}>
          <PermissionGate
            checks={[
              { module: MODULES.EQUIPMENT, action: ACTIONS.CREATE },
              { module: MODULES.EQUIPMENT, action: ACTIONS.EDIT }
            ]}
          >
            <Button
              aria-label="Manage Categories"
              className={`flex-1`}
              onClick={onManageCategoriesClick}
              color="primary"
              variant="outlined"
              disabled={!active || isPending}
            >
              {t("listings.tabs.resources.manage_categories") || "Manage Categories"}
            </Button>
          </PermissionGate>
          <PermissionGate module={MODULES.EQUIPMENT} action={ACTIONS.CREATE}>
            <Button
              aria-label={t("clients.import_client_list") || "Add Equipment"}
              onClick={onAddEquipmentClick}
              color="primary"
              variant="solid"
              disabled={!active || isPending}
            >
              {t("listings.tabs.resources.add_equipment")}
            </Button>
          </PermissionGate>
        </Flex>
      </Flex>

      <CustomDrawer open={categoryDrawerOpen} setOpen={setCategoryDrawerOpen} title={t("Select Categories") || "Select Categories"}>
        <Flex vertical gap={16} style={{ padding: "16px 0" }}>
          {categoryOptions.map((option) => (
            <Checkbox
              key={option.value}
              checked={selectedCategories.includes(option.value)}
              onChange={() => handleCategorySelect(option.value)}
              style={{ fontSize: 16 }}
            >
              {option.label}
            </Checkbox>
          ))}
        </Flex>
      </CustomDrawer>
    </>
  )
}

EquipmentFilters.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  selectedCategories: PropTypes.array.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  onManageCategoriesClick: PropTypes.func.isRequired,
  onAddEquipmentClick: PropTypes.func.isRequired
}

export default React.memo(EquipmentFilters)
